import { useEffect, useRef } from 'react'
import { Box, Flex, Text, VStack, Grid, Input, useT } from '@minetrace/ui'
import {
  LuPlus as Plus,
  LuTrash2 as Trash2,
  LuUpload as Upload,
  LuGlobe as Globe,
  LuDownload as Download,
} from 'react-icons/lu'
import { useFormColors } from '@minetrace/ui/forms'
import { AdvancedMarker, AdvancedMarkerAnchorPoint } from '@vis.gl/react-google-maps'
import { KMap, FitBounds, useMap } from '../lib/googleMaps'
import { utmToLatLng } from '../utils/utmConverter'
import { referenceRings, countOutside } from '../utils/geoReference'

type LL = google.maps.LatLngLiteral

/**
 * Polígono imperativo sobre `google.maps.Polygon` (la base reutilizable solo
 * trae `Polyline`). Mismo ciclo de vida que `lib/googleMaps/Polyline`: se crea
 * en un efecto con `setMap`, se reconfigura en otro con `setOptions` y se limpia
 * con `setMap(null)`.
 */
function Polygon({
  paths,
  fillColor,
  fillOpacity,
  strokeColor,
  strokeWeight,
}: {
  paths: LL[]
  fillColor: string
  fillOpacity: number
  strokeColor: string
  strokeWeight: number
}) {
  const map = useMap()
  const ref = useRef<google.maps.Polygon | null>(null)

  useEffect(() => {
    if (!map) return
    const poly = new google.maps.Polygon()
    poly.setMap(map)
    ref.current = poly
    return () => {
      poly.setMap(null)
      ref.current = null
    }
  }, [map])

  useEffect(() => {
    ref.current?.setOptions({
      paths,
      fillColor,
      fillOpacity,
      strokeColor,
      strokeWeight,
      clickable: false,
    })
  }, [paths, fillColor, fillOpacity, strokeColor, strokeWeight])

  return null
}

export interface Vertex {
  northing: string
  easting: string
}

export interface PolygonEditorValue {
  vertices: Vertex[]
  utmZone: number
  newNorthing: string
  newEasting: string
}

interface PolygonEditorProps {
  value: PolygonEditorValue
  onChange: (next: PolygonEditorValue) => void
  readOnly?: boolean
  mapHeight?: string
  /** Contorno oficial del DERECHO minero (INGEMMET), de fondo y como referencia: la
   *  concesión se dibuja dentro. No se edita ni se guarda desde aquí. */
  referenceGeoJson?: string | null
}

export const PolygonEditor = ({ value, onChange, readOnly, mapHeight, referenceGeoJson }: PolygonEditorProps) => {
  const c = useFormColors()
  const { t } = useT()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { vertices, utmZone, newNorthing, newEasting } = value

  const inputStyles = {
    bg: '#efe8dd',
    border: 'none',
    borderRadius: 'lg',
    color: c.text,
    h: '40px',
    fontSize: 'sm',
    fontFamily: 'monospace',
    _placeholder: { color: '#b5aa9d' },
    _focus: { outline: 'none', ring: '1px', ringColor: '#b4532a' },
  } as const

  const setZone = (z: number) => onChange({ ...value, utmZone: z })
  const setNewN = (v: string) => onChange({ ...value, newNorthing: v })
  const setNewE = (v: string) => onChange({ ...value, newEasting: v })

  const addVertex = () => {
    const n = parseFloat(newNorthing)
    const e = parseFloat(newEasting)
    if (isNaN(n) || isNaN(e)) return
    onChange({
      ...value,
      vertices: [...vertices, { northing: newNorthing, easting: newEasting }],
      newNorthing: '',
      newEasting: '',
    })
  }

  const removeVertex = (index: number) => {
    onChange({ ...value, vertices: vertices.filter((_, i) => i !== index) })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addVertex()
    }
  }

  const handleImportCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target?.result as string
      const lines = text.split('\n').filter((l) => l.trim())
      const imported: Vertex[] = []
      for (const line of lines) {
        const parts = line.split(/[,;\t]/).map((p) => p.trim())
        if (parts.length >= 2) {
          const n = parseFloat(parts[0])
          const ee = parseFloat(parts[1])
          if (!isNaN(n) && !isNaN(ee)) {
            imported.push({ northing: String(Math.round(n)), easting: String(Math.round(ee)) })
          }
        }
      }
      if (imported.length > 0) onChange({ ...value, vertices: [...vertices, ...imported] })
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  // Convert vertices to lat/lng for map
  const positions: [number, number][] = vertices
    .map((v) => {
      const n = parseFloat(v.northing)
      const ee = parseFloat(v.easting)
      if (isNaN(n) || isNaN(ee)) return null
      const { lat, lng } = utmToLatLng(n, ee, utmZone)
      return [lat, lng] as [number, number]
    })
    .filter((p): p is [number, number] => p !== null)

  const latLngs: LL[] = positions.map(([lat, lng]) => ({ lat, lng }))

  // Derecho minero de referencia. Sin concesión dibujada, el mapa encuadra el derecho
  // para que se vea dónde dibujar; con concesión, encuadra ambos.
  const refRings = referenceRings(referenceGeoJson)
  const outside = countOutside(latLngs, refRings)
  const fitPoints = [...latLngs, ...refRings.flat()]

  return (
    <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={5} pb={2}>
      {/* Left: Vertices table */}
      <Box bg="white" borderRadius="xl" border="1px solid #e8dfd1" p={5}>
        {/* Zone selector */}
        <Flex alignItems="center" gap={3} mb={4} p={3} bg="#eef7f6" borderRadius="lg" border="1px solid #bfe3df">
          <Globe size={16} color="#1d6664" />
          <Text tid="shared.georeference.utmZone" fontSize="sm" fontWeight="600" color="#2a2622" />
          {([17, 18, 19] as const).map((z) => (
            <Box
              key={z}
              as="button"
              type="button"
              px={3}
              py={1.5}
              borderRadius="md"
              fontSize="sm"
              fontWeight="700"
              cursor={readOnly ? 'default' : 'pointer'}
              bg={utmZone === z ? '#1d6664' : 'white'}
              color={utmZone === z ? 'white' : '#4a5568'}
              border="1px solid"
              borderColor={utmZone === z ? '#1d6664' : '#e8dfd1'}
              _hover={readOnly ? {} : { bg: utmZone === z ? '#085a30' : '#faf6f0' }}
              onClick={() => !readOnly && setZone(z)}
            >
              {z}S
            </Box>
          ))}
        </Flex>

        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Text tid="shared.georeference.verticesTitle" fontSize="sm" fontWeight="700" color="#2a2622" />
          {!readOnly && (
            <Flex gap={3}>
              <Box as="button" type="button" display="flex" alignItems="center" gap={1}
                fontSize="xs" fontWeight="600" color="#857b71" cursor="pointer"
                _hover={{ textDecoration: 'underline', color: '#012d1d' }}
                onClick={() => {
                  const csv = 'Northing,Easting\n8951234,234567\n8951300,234600\n8951250,234650'
                  const blob = new Blob([csv], { type: 'text/csv' })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url; a.download = 'plantilla_poligono.csv'; a.click()
                  URL.revokeObjectURL(url)
                }}>
                <Download size={14} /> {t('shared.georeference.csvTemplate')}
              </Box>
              <input type="file" accept=".csv,.txt" ref={fileInputRef} style={{ display: 'none' }} onChange={handleImportCsv} />
              <Box as="button" type="button" display="flex" alignItems="center" gap={1}
                fontSize="xs" fontWeight="600" color="#1d6664" cursor="pointer"
                _hover={{ textDecoration: 'underline' }}
                onClick={() => fileInputRef.current?.click()}>
                <Upload size={14} /> {t('shared.georeference.importCsv')}
              </Box>
            </Flex>
          )}
        </Flex>

        {/* Table header */}
        <Grid templateColumns="50px 1fr 1fr 50px" gap={2} mb={2}>
          <Text tid="shared.georeference.colPoint" fontSize="10px" fontWeight="700" color="#b5aa9d" textTransform="uppercase" letterSpacing="0.05em" />
          <Text tid="shared.georeference.colNorth" fontSize="10px" fontWeight="700" color="#b5aa9d" textTransform="uppercase" letterSpacing="0.05em" />
          <Text tid="shared.georeference.colEast" fontSize="10px" fontWeight="700" color="#b5aa9d" textTransform="uppercase" letterSpacing="0.05em" />
          <Text tid="shared.georeference.colAction" fontSize="10px" fontWeight="700" color="#b5aa9d" textTransform="uppercase" letterSpacing="0.05em" />
        </Grid>

        {/* Existing vertex rows */}
        <VStack gap={2} alignItems="stretch" mb={3}>
          {vertices.map((v, i) => (
            <Grid key={i} templateColumns="50px 1fr 1fr 50px" gap={2} alignItems="center"
              py={2} borderBottom="1px solid #efe8dd">
              <Text fontSize="sm" fontWeight="600" color="#2a2622">{i + 1}</Text>
              <Text fontSize="sm" fontFamily="monospace" color="#2a2622">
                {parseInt(v.northing).toLocaleString('en')}
              </Text>
              <Text fontSize="sm" fontFamily="monospace" color="#2a2622">
                {parseInt(v.easting).toLocaleString('en')}
              </Text>
              {!readOnly && (
                <Box as="button" type="button" display="flex" alignItems="center" justifyContent="center"
                  w="32px" h="32px" borderRadius="md" color="#b23a26" cursor="pointer"
                  _hover={{ bg: '#fce4e4' }} onClick={() => removeVertex(i)}>
                  <Trash2 size={14} />
                </Box>
              )}
            </Grid>
          ))}

          {/* New vertex input row */}
          {!readOnly && (
            <Grid templateColumns="50px 1fr 1fr 50px" gap={2} alignItems="center" py={1}>
              <Text fontSize="sm" fontWeight="600" color="#b5aa9d">{vertices.length + 1}</Text>
              <Input
                value={newNorthing}
                onChange={(e) => setNewN(e.target.value.replace(/[^0-9]/g, ''))}
                onKeyDown={handleKeyDown}
                placeholder={t('shared.georeference.placeholderNorth')}
                inputMode="numeric"
                {...inputStyles}
              />
              <Input
                value={newEasting}
                onChange={(e) => setNewE(e.target.value.replace(/[^0-9]/g, ''))}
                onKeyDown={handleKeyDown}
                placeholder={t('shared.georeference.placeholderEast')}
                inputMode="numeric"
                {...inputStyles}
              />
              <Box as="button" type="button" display="flex" alignItems="center" justifyContent="center"
                w="32px" h="32px" borderRadius="full" bg="#b4532a" color="white" cursor="pointer"
                _hover={{ bg: '#085a30' }} onClick={addVertex}>
                <Plus size={16} />
              </Box>
            </Grid>
          )}
        </VStack>

        {/* UTM note */}
        <Text tid="shared.georeference.utmNote" values={{ zone: utmZone }} fontSize="xs" color="#1d6664" fontStyle="italic" />
      </Box>

      {/* Right: Map */}
      <Box
        borderRadius="xl"
        overflow="hidden"
        border="1px solid #e8dfd1"
        h={{ base: '300px', md: mapHeight ?? '100%' }}
        minH={mapHeight ?? '400px'}
        position="relative"
      >
        <KMap defaultCenter={{ lat: -12.0464, lng: -77.0428 }} defaultZoom={6} mapTypeId="hybrid">
          <FitBounds points={fitPoints} padding={30} nonce={latLngs.length + refRings.length * 1000} />
          {refRings.map((ring, i) => (
            <Polygon key={`ref-${i}`} paths={ring} strokeColor="#d49a2a" strokeWeight={2} fillColor="#d49a2a" fillOpacity={0.06} />
          ))}
          {latLngs.length >= 3 && (
            <Polygon
              paths={latLngs}
              strokeColor="#1d6664"
              strokeWeight={2}
              fillColor="#1d6664"
              fillOpacity={0.15}
            />
          )}
          {latLngs.map((pos, i) => (
            <AdvancedMarker key={i} position={pos} anchorPoint={AdvancedMarkerAnchorPoint.CENTER}>
              <div
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: '#1d6664',
                  border: '2px solid white',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                }}
              />
            </AdvancedMarker>
          ))}
        </KMap>
        {refRings.length > 0 && (
          <Box position="absolute" left={3} bottom={3} bg="rgba(255,255,255,0.94)" borderRadius="lg" px={3} py={2}
            boxShadow="0 4px 14px rgba(0,0,0,0.18)" fontSize="11px" color="#2a2622">
            <Flex align="center" gap={2}>
              <Box w="14px" h="3px" bg="#d49a2a" borderRadius="2px" />
              <Text>{t('shared.georeference.legendOfficial')}</Text>
            </Flex>
            <Flex align="center" gap={2} mt={1}>
              <Box w="14px" h="3px" bg="#1d6664" borderRadius="2px" />
              <Text>{t('shared.georeference.legendConcession')}</Text>
            </Flex>
            {outside > 0 && (
              <Text mt={1.5} color="#b23a26" fontWeight="700">
                {t('shared.georeference.verticesOutside', { count: outside })}
              </Text>
            )}
          </Box>
        )}
      </Box>
    </Grid>
  )
}

export default PolygonEditor
